import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { contractApi, quoteApi, absoluteFileUrl } from "@/lib/phase3Entities";
import { apiError } from "@/lib/errors";
import { Lock, Pencil, Trash2, Check, X } from "lucide-react";
import { ActionMenu } from "@/components/ActionMenu";
import { LinkedContactPicker } from "@/components/LinkedContactPicker";

const STATUS_STYLES = {
  draft: "bg-chrome-mist text-carbon border-coach-silver",
  generated: "bg-coach-blue/10 text-coach-blue border-coach-blue",
  sent: "bg-coach-blue/10 text-coach-blue border-coach-blue",
  awaiting_signature: "bg-amber-hold/20 text-carbon border-amber-hold",
  executed: "bg-touring-lime/25 text-carbon border-touring-lime",
  superseded: "bg-steel/10 text-steel border-steel",
  void: "bg-critical-red/10 text-critical-red border-critical-red",
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

const rowDescription = (r) => (r.description && r.description.trim()) || r.label || "—";

export default function ContractsTab({ tourId, canWrite = true, isSuperAdmin = false, customerId = null }) {
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [deposit, setDeposit] = useState("");
  const [busy, setBusy] = useState(false);
  const [billingContact, setBillingContact] = useState(null);
  // RC10c · accepted-quote selector state. Backend Q15 gate requires
  // `source_quote_id` of a quote whose status === "accepted" on this
  // tour. We fetch all quotes, filter to accepted, auto-pick when
  // exactly one exists, and disable Generate when zero exist.
  const [acceptedQuotes, setAcceptedQuotes] = useState([]);
  const [sourceQuoteId, setSourceQuoteId] = useState("");
  // Manual payment-schedule form drafts, per-contract.
  const [scheduleDraft, setScheduleDraft] = useState({});
  // Add-row in-flight per contract (double-click guard).
  const [addingRow, setAddingRow] = useState({});
  // Inline edit state, per-row: {rowId: {description, due_date, scheduled_amount}}
  const [editRow, setEditRow] = useState({});
  // Row-mutation in-flight, per row id.
  const [rowBusy, setRowBusy] = useState({});

  const load = async () => {
    try {
      setContracts(await contractApi.list(tourId));
      const tpls = await contractApi.templates();
      setTemplates(tpls);
      if (!templateId && tpls[0]) setTemplateId(tpls[0].id);
      // RC10c · load accepted quotes for the required source-quote selector.
      const quotes = await quoteApi.list(tourId);
      const accepted = (Array.isArray(quotes) ? quotes : []).filter(
        (q) => q.status === "accepted"
      );
      setAcceptedQuotes(accepted);
      // Auto-pick ONLY when exactly one accepted quote exists.
      // Preserve existing selection if it is still accepted.
      setSourceQuoteId((prev) => {
        if (prev && accepted.some((q) => q.id === prev)) return prev;
        if (accepted.length === 1) return accepted[0].id;
        return "";
      });
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tourId]);

  const create = async () => {
    if (!templateId) return toast.error("Pick a template");
    if (!sourceQuoteId) return toast.error("Pick an accepted Quote");
    setBusy(true);
    try {
      await contractApi.create(tourId, {
        template_id: templateId,
        source_quote_id: sourceQuoteId,
        deposit_amount: deposit || undefined,
        billing_contact_id: billingContact?.id || undefined,
      });
      toast.success("Contract generated");
      setDeposit("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const setStatus = async (id, s) => {
    let reason = null;
    if (s === "void") {
      reason = window.prompt("Void reason?") || "";
      if (!reason) return;
    }
    try { await contractApi.setStatus(id, s, reason); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addPaymentRow = async (contractId) => {
    if (addingRow[contractId]) return; // double-click guard
    const draft = scheduleDraft[contractId] || {};
    const description = (draft.description || "").trim();
    if (!description) return toast.error("Description required");
    const amountNum = Number(draft.scheduled_amount);
    if (!draft.scheduled_amount || Number.isNaN(amountNum) || amountNum <= 0) {
      return toast.error("Amount must be greater than zero");
    }
    if (!draft.due_date) return toast.error("Due date required");
    setAddingRow((s) => ({ ...s, [contractId]: true }));
    try {
      await contractApi.addPaymentRow(contractId, {
        description,
        label: description,
        due_date: draft.due_date,
        scheduled_amount: draft.scheduled_amount,
      });
      setScheduleDraft({ ...scheduleDraft, [contractId]: {} });
      toast.success("Payment row added");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAddingRow((s) => ({ ...s, [contractId]: false })); }
  };

  const beginEdit = (row) => {
    setEditRow((s) => ({
      ...s,
      [row.id]: {
        description: rowDescription(row),
        due_date: row.due_date || "",
        scheduled_amount: row.scheduled_amount ?? "",
      },
    }));
  };

  const cancelEdit = (rowId) => {
    setEditRow((s) => {
      const next = { ...s };
      delete next[rowId];
      return next;
    });
  };

  const saveEdit = async (contractId, rowId) => {
    if (rowBusy[rowId]) return;
    const patch = editRow[rowId] || {};
    const description = (patch.description || "").trim();
    if (!description) return toast.error("Description required");
    const amountNum = Number(patch.scheduled_amount);
    if (!patch.scheduled_amount || Number.isNaN(amountNum) || amountNum <= 0) {
      return toast.error("Amount must be greater than zero");
    }
    if (!patch.due_date) return toast.error("Due date required");
    setRowBusy((s) => ({ ...s, [rowId]: true }));
    try {
      await contractApi.editPaymentRow(contractId, rowId, {
        description,
        label: description,
        due_date: patch.due_date,
        scheduled_amount: patch.scheduled_amount,
      });
      cancelEdit(rowId);
      toast.success("Payment row updated");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRowBusy((s) => ({ ...s, [rowId]: false })); }
  };

  const removeRow = async (contractId, rowId) => {
    if (rowBusy[rowId]) return;
    if (!window.confirm("Remove this payment schedule row?")) return;
    setRowBusy((s) => ({ ...s, [rowId]: true }));
    try {
      await contractApi.removePaymentRow(contractId, rowId);
      cancelEdit(rowId);
      toast.success("Payment row removed");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRowBusy((s) => ({ ...s, [rowId]: false })); }
  };

  const markPaid = async (contractId, rowId) => {
    try { await contractApi.markPaid(contractId, rowId, null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="contracts-tab">
      {canWrite && (
        <div className="bg-white border border-coach-silver rounded-md p-4" data-testid="contracts-create-panel">
          <div className="text-[10.5px] uppercase tracking-widest text-steel mb-2">Generate contract from template</div>
          <div className="grid md:grid-cols-4 gap-2 items-end">
            <label className="block md:col-span-2">
              <div className="text-[10.5px] text-steel mb-1">Template</div>
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px] bg-white"
                data-testid="contract-template-select">
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label className="block md:col-span-2">
              <div className="text-[10.5px] text-steel mb-1">
                Accepted Quote <span className="text-critical-red">*</span>
              </div>
              <select
                value={sourceQuoteId}
                onChange={(e) => setSourceQuoteId(e.target.value)}
                disabled={acceptedQuotes.length === 0}
                className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px] bg-white disabled:bg-chrome-mist disabled:text-steel"
                data-testid="contract-source-quote-select">
                {acceptedQuotes.length === 0 && (
                  <option value="">No accepted Quote on this Tour</option>
                )}
                {acceptedQuotes.length > 1 && !sourceQuoteId && (
                  <option value="">Select an accepted Quote…</option>
                )}
                {acceptedQuotes.map((q) => {
                  const total = q?.snapshot_pricing?.totals?.customer_total;
                  const totalStr = fmtMoney(total);
                  const accAt = (q.accepted_at || q.updated_at || "").replace("T", " ").slice(0, 10);
                  return (
                    <option
                      key={q.id}
                      value={q.id}
                      data-testid={`contract-source-quote-option-${q.id}`}>
                      Quote v{q.version} — {totalStr}{accAt ? ` · accepted ${accAt}` : ""}
                    </option>
                  );
                })}
              </select>
            </label>
            <label className="block">
              <div className="text-[10.5px] text-steel mb-1">Deposit (optional)</div>
              <input value={deposit} onChange={(e) => setDeposit(e.target.value)}
                placeholder="e.g. 5000"
                className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]"
                data-testid="contract-deposit" />
            </label>
            <button onClick={create} disabled={busy || acceptedQuotes.length === 0 || !sourceQuoteId}
              className="bg-coach-blue text-white text-[12.5px] font-medium px-3 py-2 rounded-sm disabled:opacity-40"
              data-testid="contract-create">
              {busy ? "Generating…" : "Generate Contract"}
            </button>
          </div>
          {acceptedQuotes.length === 0 && (
            <div className="text-[11px] text-steel mt-2" data-testid="contract-no-accepted-quote-hint">
              Accept a Quote on the Quotes tab first.
            </div>
          )}
          <div className="text-[11px] text-steel mt-2">
            Deep-copies pricing + assets + drivers. Once <strong>executed</strong>, edits to financials or snapshots are permanently locked.
          </div>
          <div className="mt-3" data-testid="contract-billing-contact-picker-wrap">
            <LinkedContactPicker
              label="Billing Contact (optional — frozen at generation)"
              testidPrefix="contract-billing-contact"
              customerId={customerId}
              value={billingContact}
              onChange={(c) => setBillingContact(c)}
            />
          </div>
        </div>
      )}

      <div className="space-y-3">
        {contracts.length === 0 && (
          <div className="bg-white border border-coach-silver rounded-md p-6 text-center text-steel text-[13px]">
            No contract versions yet.
          </div>
        )}
        {contracts.map((c) => {
          const executed = c.status === "executed";
          const draft = scheduleDraft[c.id] || {};
          return (
            <div key={c.id} data-testid={`contract-${c.id}`}
              className={`bg-white border rounded-md ${executed ? "border-touring-lime" : "border-coach-silver"}`}>
              <div className="p-4 flex items-center gap-4 border-b border-coach-silver/60">
                <div className="w-16 text-center">
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Version</div>
                  <div className="font-display text-2xl text-deep-road-navy">v{c.version}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-carbon flex items-center gap-2 flex-wrap">
                    <span className={`inline-block px-2 py-0.5 border rounded-sm text-[10.5px] uppercase tracking-widest ${STATUS_STYLES[c.status] || ""}`}
                      data-testid={`contract-status-${c.id}`}>
                      {c.status.replaceAll("_", " ")}
                    </span>
                    {executed && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-touring-lime bg-carbon px-2 py-0.5 rounded-sm"
                        data-testid={`contract-executed-lock-${c.id}`}>
                        <Lock size={12} /> IMMUTABLE
                      </span>
                    )}
                    <span className="text-steel">Total <strong className="text-carbon">{fmtMoney(c.contract_total)}</strong></span>
                    <span className="text-steel">Deposit <strong className="text-carbon">{fmtMoney(c.deposit_amount)}</strong></span>
                    <span className="text-steel">Balance <strong className="text-carbon">{fmtMoney(c.balance_amount)}</strong></span>
                  </div>
                  <div className="text-[11px] text-steel mt-1 font-mono">SHA-256 {c.pdf_sha256?.slice(0, 24)}…</div>
                </div>
                <div className="flex items-center gap-2">
                  {c.pdf_url && (
                    <a href={absoluteFileUrl(c.pdf_url)} target="_blank" rel="noreferrer"
                      className="text-coach-blue hover:underline text-[12.5px]"
                      data-testid={`contract-pdf-${c.id}`}>View PDF</a>
                  )}
                  {canWrite && c.status === "generated" && (
                    <button onClick={() => setStatus(c.id, "awaiting_signature")}
                      className="border border-amber-hold text-carbon bg-amber-hold/15 text-[12px] px-2 py-1 rounded-sm"
                      data-testid={`contract-send-${c.id}`}>Send for signature</button>
                  )}
                  {canWrite && !executed && c.status !== "void" && (
                    <button onClick={() => setStatus(c.id, "executed")}
                      className="border border-touring-lime bg-touring-lime/25 text-carbon text-[12px] px-2 py-1 rounded-sm"
                      data-testid={`contract-execute-${c.id}`}>Mark Executed</button>
                  )}
                  {isSuperAdmin && !executed && c.status !== "void" && (
                    <button onClick={() => setStatus(c.id, "void")}
                      className="border border-critical-red text-critical-red text-[12px] px-2 py-1 rounded-sm"
                      data-testid={`contract-void-${c.id}`}>Void</button>
                  )}
                  {isSuperAdmin && executed && (
                    <button onClick={() => setStatus(c.id, "void")}
                      className="border border-critical-red text-critical-red text-[12px] px-2 py-1 rounded-sm"
                      data-testid={`contract-void-${c.id}`}>Void</button>
                  )}
                  {canWrite && (
                    <ActionMenu
                      testid={`contract-actions-${c.id}`}
                      onView={c.pdf_url ? () => window.open(absoluteFileUrl(c.pdf_url), "_blank") : undefined}
                      onArchive={
                        // Only DRAFTS discardable. Any other status is a
                        // frozen snapshot → Supersede only.
                        c.status === "draft"
                          ? async () => {
                              if (!window.confirm(`Discard draft Contract v${c.version}? This cannot be undone.`)) return;
                              try {
                                await contractApi.discardDraft(c.id);
                                toast.success(`Draft v${c.version} discarded`);
                                load();
                              } catch (e) { toast.error(apiError(e)); }
                            }
                          : ["generated", "sent", "awaiting_signature", "executed", "superseded", "cancelled", "void"].includes(c.status)
                            ? async () => {
                                if (!window.confirm(`Supersede this contract and create a new v${c.version + 1} draft?`)) return;
                                if (!sourceQuoteId) {
                                  toast.error("Pick an accepted Quote before superseding");
                                  return;
                                }
                                try {
                                  if (c.status !== "superseded") await contractApi.setStatus(c.id, "superseded", "user_superseded");
                                  await contractApi.create(tourId, {
                                    template_id: c.template_id,
                                    source_quote_id: sourceQuoteId,
                                  });
                                  toast.success("New draft created");
                                  load();
                                } catch (e) { toast.error(apiError(e)); }
                              }
                            : undefined
                      }
                      archiveLabel={c.status === "draft" ? "Discard Draft" : "Supersede →"}
                    />
                  )}
                </div>
              </div>

              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10.5px] uppercase tracking-widest text-steel">Payment Schedule</div>
                  {executed && (
                    <div
                      className="inline-flex items-center gap-1 text-[11px] text-steel"
                      title="Payment schedule is locked — this contract is executed."
                      data-testid={`payment-schedule-locked-${c.id}`}>
                      <Lock size={12} /> Locked — contract executed
                    </div>
                  )}
                </div>
                <table className="w-full text-[13px]">
                  <thead className="bg-road-white text-[10px] uppercase tracking-widest text-steel">
                    <tr>
                      <th className="text-left px-3 py-1.5">Description</th>
                      <th className="text-left px-3 py-1.5 w-36">Due Date</th>
                      <th className="text-right px-3 py-1.5 w-32">Amount</th>
                      <th className="text-left px-3 py-1.5 w-24">Status</th>
                      <th className="w-40" />
                    </tr>
                  </thead>
                  <tbody>
                    {(c.payment_schedule || []).map((r) => {
                      const editing = !!editRow[r.id];
                      const rBusy = !!rowBusy[r.id];
                      const canEditRow = canWrite && !executed && r.status !== "paid";
                      if (editing) {
                        const ed = editRow[r.id];
                        return (
                          <tr key={r.id} className="border-t border-coach-silver/60 bg-road-white/40" data-testid={`payment-row-${r.id}`}>
                            <td className="px-3 py-1.5">
                              <input value={ed.description}
                                onChange={(e) => setEditRow((s) => ({ ...s, [r.id]: { ...s[r.id], description: e.target.value } }))}
                                className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px]"
                                data-testid={`payment-edit-description-${r.id}`} />
                            </td>
                            <td className="px-3 py-1.5">
                              <input type="date" value={ed.due_date}
                                onChange={(e) => setEditRow((s) => ({ ...s, [r.id]: { ...s[r.id], due_date: e.target.value } }))}
                                className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px]"
                                data-testid={`payment-edit-due-${r.id}`} />
                            </td>
                            <td className="px-3 py-1.5">
                              <input type="number" min="0" step="0.01" value={ed.scheduled_amount}
                                onChange={(e) => setEditRow((s) => ({ ...s, [r.id]: { ...s[r.id], scheduled_amount: e.target.value } }))}
                                className="w-full border border-coach-silver rounded-sm px-2 py-1 text-[13px] text-right"
                                data-testid={`payment-edit-amount-${r.id}`} />
                            </td>
                            <td className="px-3 py-1.5 text-steel">{r.status}</td>
                            <td className="px-3 py-1.5 text-right">
                              <button onClick={() => saveEdit(c.id, r.id)} disabled={rBusy}
                                className="inline-flex items-center gap-1 text-coach-blue hover:underline text-[12px] mr-3 disabled:opacity-40"
                                data-testid={`payment-edit-save-${r.id}`}>
                                <Check size={12} /> {rBusy ? "Saving…" : "Save"}
                              </button>
                              <button onClick={() => cancelEdit(r.id)} disabled={rBusy}
                                className="inline-flex items-center gap-1 text-steel hover:underline text-[12px] disabled:opacity-40"
                                data-testid={`payment-edit-cancel-${r.id}`}>
                                <X size={12} /> Cancel
                              </button>
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={r.id} className="border-t border-coach-silver/60" data-testid={`payment-row-${r.id}`}>
                          <td className="px-3 py-1.5 text-carbon" data-testid={`payment-description-${r.id}`}>
                            {rowDescription(r)}
                          </td>
                          <td className="px-3 py-1.5 text-steel" data-testid={`payment-due-display-${r.id}`}>
                            {r.due_date || "—"}
                          </td>
                          <td className="px-3 py-1.5 text-carbon text-right" data-testid={`payment-amount-display-${r.id}`}>
                            {fmtMoney(r.scheduled_amount)}
                          </td>
                          <td className="px-3 py-1.5 text-steel">{r.status}</td>
                          <td className="px-3 py-1.5 text-right whitespace-nowrap">
                            {r.status !== "paid" && (
                              <button onClick={() => markPaid(c.id, r.id)}
                                className="text-coach-blue hover:underline text-[12px] mr-3"
                                data-testid={`payment-mark-paid-${r.id}`}>Mark Paid</button>
                            )}
                            {canEditRow ? (
                              <>
                                <button onClick={() => beginEdit(r)} disabled={rBusy}
                                  className="inline-flex items-center gap-1 text-carbon hover:underline text-[12px] mr-3 disabled:opacity-40"
                                  data-testid={`payment-edit-${r.id}`}>
                                  <Pencil size={12} /> Edit
                                </button>
                                <button onClick={() => removeRow(c.id, r.id)} disabled={rBusy}
                                  className="inline-flex items-center gap-1 text-critical-red hover:underline text-[12px] disabled:opacity-40"
                                  data-testid={`payment-remove-${r.id}`}>
                                  <Trash2 size={12} /> Remove
                                </button>
                              </>
                            ) : executed ? (
                              <span
                                className="inline-flex items-center gap-1 text-[11px] text-steel"
                                title="Payment schedule is locked — this contract is executed."
                                data-testid={`payment-row-locked-${r.id}`}>
                                <Lock size={11} /> Locked
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                    {(!c.payment_schedule || c.payment_schedule.length === 0) && (
                      <tr>
                        <td colSpan={5} className="px-3 py-4 text-center text-steel"
                          data-testid={`payment-empty-${c.id}`}>
                          No payments scheduled.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {canWrite && !executed && (
                  <div className="mt-3" data-testid={`payment-add-${c.id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end">
                      <label className="md:col-span-3 block">
                        <div className="text-[10.5px] text-steel mb-1">Description</div>
                        <input value={draft.description || ""}
                          onChange={(e) => setScheduleDraft({ ...scheduleDraft, [c.id]: { ...draft, description: e.target.value } })}
                          placeholder="e.g. Deposit — 25%"
                          className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]"
                          data-testid={`payment-description-${c.id}`} />
                      </label>
                      <label className="block">
                        <div className="text-[10.5px] text-steel mb-1">Due Date</div>
                        <input type="date" value={draft.due_date || ""}
                          onChange={(e) => setScheduleDraft({ ...scheduleDraft, [c.id]: { ...draft, due_date: e.target.value } })}
                          className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]"
                          data-testid={`payment-due-${c.id}`} />
                      </label>
                      <label className="block">
                        <div className="text-[10.5px] text-steel mb-1">Amount (USD)</div>
                        <input type="number" min="0" step="0.01" value={draft.scheduled_amount || ""}
                          onChange={(e) => setScheduleDraft({ ...scheduleDraft, [c.id]: { ...draft, scheduled_amount: e.target.value } })}
                          placeholder="0.00"
                          className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px] text-right"
                          data-testid={`payment-amount-${c.id}`} />
                      </label>
                      <button onClick={() => addPaymentRow(c.id)} disabled={!!addingRow[c.id]}
                        className="bg-coach-blue text-white text-[12.5px] font-medium px-3 py-2 rounded-sm disabled:opacity-40"
                        data-testid={`payment-add-btn-${c.id}`}>
                        {addingRow[c.id] ? "Saving…" : "Add Row"}
                      </button>
                    </div>
                    <div className="text-[11px] text-steel mt-2">
                      Staff-authored schedule. Nothing is auto-generated — dates and amounts are entered manually.
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
